import { Image, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import authService from '../api-authorization/AuthorizeService';

export interface AuthenticatedAttachmentImageProps {
    attachmentId: number;
    alt?: string;
    width?: number;
    height?: number;
    className?: string;
    preview?: boolean;
}

/** Loads attachment binary with Bearer token (browser img src cannot send auth). */
const AuthenticatedAttachmentImage: React.FC<AuthenticatedAttachmentImageProps> = ({
    attachmentId,
    alt,
    width = 56,
    height = 56,
    className,
    preview = true,
}) => {
    const [src, setSrc] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);
    const urlRef = useRef<string | undefined>();

    useEffect(() => {
        let cancelled = false;
        urlRef.current = undefined;
        setLoading(true);
        setSrc(undefined);

        (async () => {
            try {
                const token = await authService.getAccessToken();
                const res = await fetch(
                    `${window.location.origin}/api/Attachments/${attachmentId}/Download`,
                    {
                        headers: { authorization: `Bearer ${token}` },
                    }
                );
                if (!res.ok || cancelled) return;
                const blob = await res.blob();
                if (cancelled) return;
                const u = URL.createObjectURL(blob);
                urlRef.current = u;
                setSrc(u);
            } catch {
                if (!cancelled) setSrc(undefined);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            if (urlRef.current) {
                URL.revokeObjectURL(urlRef.current);
                urlRef.current = undefined;
            }
        };
    }, [attachmentId]);

    if (loading) {
        return <Spin size="small" />;
    }

    if (!src) {
        return null;
    }

    return (
        <Image
            src={src}
            alt={alt || ''}
            width={width}
            height={height}
            className={className}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            preview={preview ? {} : false}
        />
    );
};

export default AuthenticatedAttachmentImage;
